import type { ReactNode } from 'react';

import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Icon } from '#/components/Icon/Icon';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { isAdmin } from '#/utils/userEnvironment';

import type {
  HamburgerMenuExpandableRowType,
  HamburgerMenuIcon,
  HamburgerMenuLinkRow,
  HamburgerMenuRow as HamburgerMenuRowData,
} from './data/types/HamburgerMenuData';
import {
  HamburgerMenuExpandableButton,
  HamburgerMenuLink,
  HamburgerMenuRowChevronHolder,
  HamburgerMenuRowIconHolder,
  HamburgerMenuRowLabel,
  HamburgerMenuRowsHolder,
} from './HamburgerMenu.styled';
import { hamburgerMenuConstantsByRowType } from './hamburgerMenuConstants';

type Props = {
  row: HamburgerMenuRowData;
  // 'Hamburger Menu' for top-level rows, 'Hamburger Menu > <Section>' inside an expandable
  trackingContext: string;
  expandedRowTypes: ReadonlySet<HamburgerMenuExpandableRowType>;
  onToggleExpanded: (type: HamburgerMenuExpandableRowType) => void;
};

function HamburgerMenuRowIcon({ icon }: { icon: HamburgerMenuIcon }) {
  switch (icon.kind) {
    case 'icon':
      return <Icon name={icon.name} size={20} />;
    case 'image':
      return <img src={getStaticImageSrc(icon.src)} alt="" loading="lazy" />;
    case 'none':
      return null;
  }
}

function getLinkRowHref(row: HamburgerMenuLinkRow, user: ApiUser | null) {
  if (!user) return row.loggedOutHref ?? row.href;
  if (isAdmin(user)) return row.adminUserHref ?? row.href;

  return row.href;
}

type RowContentProps = {
  icon: HamburgerMenuIcon;
  title: ReactNode;
};

export function HamburgerMenuRowContent({ icon, title }: RowContentProps) {
  return (
    <>
      <HamburgerMenuRowIconHolder aria-hidden>
        <HamburgerMenuRowIcon icon={icon} />
      </HamburgerMenuRowIconHolder>
      <HamburgerMenuRowLabel>{title}</HamburgerMenuRowLabel>
    </>
  );
}

export function HamburgerMenuRow({
  row,
  trackingContext,
  expandedRowTypes,
  onToggleExpanded,
}: Props) {
  const { user } = useUser();
  const { icon, qaId, trackingName } =
    hamburgerMenuConstantsByRowType[row.type];

  if ('href' in row) {
    const href = getLinkRowHref(row, user);

    return (
      <li>
        <HamburgerMenuLink
          type="link"
          href={href}
          anchorType={row.fullPageReload ? 'native' : 'router'}
          qaId={qaId}
          clickActions={{
            closeRichDetailsFromWithin: true,
            track: {
              name: trackingName,
              properties: { context: trackingContext },
            },
          }}
        >
          <HamburgerMenuRowContent icon={icon} title={row.title} />
        </HamburgerMenuLink>
      </li>
    );
  }

  const expanded = expandedRowTypes.has(row.type);
  const childrenId = `hamburger-menu-${row.type}-rows`;

  const onClick = () => {
    // Expand only: collapsing isn't tracked
    if (!expanded) {
      trackEvent(`Expanded: ${trackingName}`, { context: trackingContext });
    }
    onToggleExpanded(row.type);
  };

  return (
    <li>
      <HamburgerMenuExpandableButton
        type="button"
        aria-expanded={expanded}
        aria-controls={childrenId}
        data-qa-id={qaId}
        onClick={onClick}
      >
        <HamburgerMenuRowContent icon={icon} title={row.title} />
        <HamburgerMenuRowChevronHolder aria-hidden>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={12} />
        </HamburgerMenuRowChevronHolder>
      </HamburgerMenuExpandableButton>

      <HamburgerMenuRowsHolder
        id={childrenId}
        data-nested
        hidden={!expanded}
        data-qa-id={childrenId}
      >
        {row.rows.map((childRow) => (
          <HamburgerMenuRow
            key={childRow.type}
            row={childRow}
            trackingContext={`${trackingContext} > ${trackingName}`}
            expandedRowTypes={expandedRowTypes}
            onToggleExpanded={onToggleExpanded}
          />
        ))}
      </HamburgerMenuRowsHolder>
    </li>
  );
}
